import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import MovieIcon from '@mui/icons-material/MovieFilterOutlined';
import EditIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useTranslation } from '../../i18n';
import { format } from '../../i18n/I18nProvider';
import type { Attachment, GenerationKind } from '../../lib/agent';
import { agentJobs, formatElapsed, jobEstimate, useAgentJob, type TrackedJob } from '../../lib/agentJobs';

// Ticks once a second while `on`, so elapsed / remaining times count live.
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [on]);
  return now;
}

function JobProgress({ job, kind }: { job: TrackedJob | undefined; kind: GenerationKind }) {
  const t = useTranslation().videoAgent;
  const now = useNow(true);
  const est = job ? jobEstimate(job, now) : null;
  const parts: string[] = [];
  if (est?.queuePosition) parts.push(format(t.queuePosition, { n: est.queuePosition }));
  if (est?.remainingSec != null) {
    parts.push(est.remainingSec < 45 ? t.etaAlmost : format(t.etaLeft, { min: Math.max(1, Math.round(est.remainingSec / 60)) }));
  } else if (est && est.elapsedSec > 0) {
    parts.push(format(t.elapsed, { time: formatElapsed(est.elapsedSec) }), kind === 'video' ? t.typicalVideo : t.typicalImage);
  }
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
        <Chip
          size="small"
          color={est?.phase === 'rendering' ? 'primary' : est?.phase === 'done' ? 'success' : 'default'}
          variant="outlined"
          label={est?.phase === 'rendering' ? t.rendering : est?.phase === 'done' ? t.jobDone : t.queued}
        />
        {parts.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {parts.join(' · ')}
          </Typography>
        )}
      </Stack>
      <LinearProgress
        variant={est?.progress != null ? 'determinate' : 'indeterminate'}
        value={est?.progress ?? 0}
        sx={{ height: 6, borderRadius: 1 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {kind === 'video' ? t.leaveNoteVideo : t.leaveNoteImage}
      </Typography>
      {job?.pollError && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
          {job.status === null ? format(t.statusUnavailable, { error: job.pollError }) : job.pollError}
        </Typography>
      )}
    </Box>
  );
}

// A generation submitted from the chat. Tracking lives in lib/agentJobs, so
// the job keeps being followed while the user is elsewhere in the app; this
// card only shows it: status and estimate while it runs, then the result.
export function AgentJobCard({
  activityId,
  kind,
  onUseImage,
}: {
  activityId: number;
  kind: GenerationKind;
  onUseImage: (a: Attachment, action: 'animate' | 'edit') => void;
}) {
  const t = useTranslation().videoAgent;
  const job = useAgentJob({ activityId, kind });

  // Shown here, so the nav indicator can stop announcing it.
  const unseen = job?.unseen;
  useEffect(() => {
    if (unseen) agentJobs.markSeen(activityId);
  }, [unseen, activityId]);

  if (job?.status === 'failed') {
    return <Alert severity="error">{job.error ?? t.failed}</Alert>;
  }

  const urls = job?.results ?? [];
  if (job?.status !== 'completed' || !urls.length) {
    return <JobProgress job={job} kind={kind} />;
  }

  if (kind === 'video') {
    return (
      <Stack spacing={1}>
        <Box
          component="video"
          src={urls[0].url}
          controls
          playsInline
          preload="metadata"
          sx={{ width: '100%', maxHeight: 480, borderRadius: 1.5, bgcolor: 'black' }}
        />
        <Box>
          <Button size="small" startIcon={<DownloadIcon />} href={urls[0].url} download>
            {t.download}
          </Button>
        </Box>
      </Stack>
    );
  }

  const asAttachment = (u: { url: string; key: string }): Attachment => ({
    kind: 'image',
    r2_key: u.key,
    url: u.url,
    name: 'generated.png',
    uploaded_at: new Date().toISOString(),
  });

  return (
    <Stack spacing={1.5}>
      {urls.map((u) => (
        <Stack key={u.url} spacing={1}>
          <Box
            component="img"
            src={u.url}
            alt=""
            sx={{ width: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 1.5, bgcolor: 'action.hover' }}
          />
          {u.key && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button size="small" variant="contained" startIcon={<MovieIcon />} onClick={() => onUseImage(asAttachment(u), 'animate')}>
                {t.animate}
              </Button>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onUseImage(asAttachment(u), 'edit')}>
                {t.editImage}
              </Button>
              <Button size="small" startIcon={<DownloadIcon />} href={u.url} download>
                {t.download}
              </Button>
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
